import { useCallback, useEffect, useRef, useState } from "react";
import lib from "../assets/utils/lib";

/** @deprecated Use `lib.normalizeUnifiedSearchResults` — v2 returns `data` as a top-level array only. */
export function normalizeUnifiedSearchResults(data) {
  return lib.normalizeUnifiedSearchResults(data);
}

/** Recommended debounce (ms) for typeaheads calling GET /v2/search (UI integration guide). */
export const UNIFIED_SEARCH_TYPEAHEAD_DEBOUNCE_MS = 350;

/**
 * Shared GET /v2/search execution: abort in-flight requests, optional managed UI state, 403 messaging.
 * Canonical API reference: `docs/V2_UNIFIED_SEARCH_UI.md` (backend repo).
 *
 * @param {{ manageLoading?: boolean }} [options]
 */
export function useUnifiedSearch(options = {}) {
  const { manageLoading = true } = options;
  const abortRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [lastBody, setLastBody] = useState(null);
  /** Nullable API `search` object; only show "filtered by …" when non-null (per unified search UI guide). */
  const [searchMeta, setSearchMeta] = useState(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    cancel();
    if (manageLoading) setLoading(false);
    setResults([]);
    setFeedback(null);
    setPagination(null);
    setLastBody(null);
    setSearchMeta(null);
  }, [cancel, manageLoading]);

  useEffect(() => () => cancel(), [cancel]);

  const execute = useCallback(
    async ({
      target,
      resource: explicitResource,
      search = "",
      page = 1,
      pageSize,
      extra = {},
      catalogEntry,
      validate = true,
      omitState = false,
    } = {}) => {
      const resource =
        explicitResource ||
        (target ? lib.unifiedSearchTargetToResource(target) : null);
      if (!resource) {
        const msg = "Invalid search target.";
        if (!omitState) {
          setFeedback(msg);
          setResults([]);
        }
        return { ok: false, validation: msg, resource: null };
      }

      const q = String(search ?? "").trim();
      const rules = lib.effectiveUnifiedSearchRules(resource, catalogEntry);

      if (lib.unifiedSearchRequiresFacilityId(resource)) {
        const fid = extra?.facility_id;
        const bad =
          fid == null ||
          String(fid).trim() === "" ||
          Number.isNaN(Number(fid)) ||
          Number(fid) < 1;
        if (bad) {
          const msg = "Facility is required for staff or tariff search.";
          if (!omitState) {
            setFeedback(msg);
            setResults([]);
            setPagination(null);
            setLastBody(null);
            setSearchMeta(null);
          }
          return { ok: false, validation: msg, resource };
        }
      }

      if (validate) {
        if (!rules.allowEmpty && !q) {
          if (!omitState) {
            setResults([]);
            setPagination(null);
            setLastBody(null);
            setSearchMeta(null);
            setFeedback(null);
          }
          return { ok: false, validation: "empty", resource };
        }
        if (q && rules.minWhenPresent > 0 && q.length < rules.minWhenPresent) {
          const msg = `Enter at least ${rules.minWhenPresent} character${
            rules.minWhenPresent === 1 ? "" : "s"
          } for this search type.`;
          if (!omitState) {
            setFeedback(msg);
            setResults([]);
            setPagination(null);
            setLastBody(null);
            setSearchMeta(null);
          }
          return { ok: false, validation: msg, resource };
        }
        if (!omitState) setFeedback(null);
      }

      cancel();
      const ac = new AbortController();
      abortRef.current = ac;

      const resolvedPageSize = lib.unifiedSearchResolvePageSize(
        resource,
        catalogEntry,
        pageSize,
      );

      if (manageLoading) setLoading(true);
      try {
        const res = await lib.unifiedSearch(
          {
            target: explicitResource ? undefined : target,
            resource: explicitResource || undefined,
            search: q,
            page,
            pageSize: resolvedPageSize,
            extra,
          },
          ac.signal,
        );
        if (ac.signal.aborted) {
          return { ok: false, aborted: true, resource };
        }

        const body = res?.data;
        const err = body?.error;
        const ok = body && (err === 0 || err === "0" || Number(err) === 0);
        if (ok) {
          const parsed = lib.parseUnifiedSearchSuccessBody(body);
          const canonicalResource = parsed.resource || resource;
          if (!omitState) {
            setResults(parsed.data);
            setPagination(parsed.pagination);
            setLastBody(parsed.raw);
            setSearchMeta(parsed.search);
            setFeedback(null);
          }
          return {
            ok: true,
            data: parsed.data,
            body: parsed.raw,
            resource: canonicalResource,
            pagination: parsed.pagination,
            search: parsed.search,
            statusContractVersion: parsed.statusContractVersion,
            scope: parsed.scope,
          };
        }

        const allowed = body?.data?.allowed;
        const msg =
          Array.isArray(allowed) && allowed.length
            ? `${body?.message || "Search failed."} Allowed: ${allowed.join(", ")}.`
            : body?.message || "Search failed.";
        if (!omitState) {
          setResults([]);
          setPagination(null);
          setLastBody(body);
          setSearchMeta(null);
          setFeedback(msg);
        }
        return { ok: false, error: msg, body, resource };
      } catch (e) {
        if (e?.name === "AbortError" || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
          return { ok: false, aborted: true, resource };
        }
        const status = e?.response?.status;
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          String(e?.message || "").toLowerCase().includes("timeout");
        const msg = isTimeout
          ? "Search timed out. Try a narrower query or fewer filters."
          : e?.response?.data?.message ||
            e?.response?.data?.msg ||
            e?.message ||
            "Search failed.";
        if (!omitState) {
          setResults([]);
          setPagination(null);
          setLastBody(null);
          setSearchMeta(null);
          if (status === 403) {
            setFeedback("You do not have access to this search.");
          } else {
            setFeedback(msg);
          }
        }
        return { ok: false, error: msg, status, resource };
      } finally {
        if (!ac.signal.aborted && manageLoading) {
          setLoading(false);
        }
      }
    },
    [cancel, manageLoading],
  );

  return {
    execute,
    cancel,
    reset,
    loading,
    results,
    setResults,
    feedback,
    setFeedback,
    pagination,
    lastBody,
    searchMeta,
    setSearchMeta,
    setPagination,
    setLastBody,
  };
}
